import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown } from 'lucide-react'

export type RowActionItem =
  | {
      key: string
      label: string
      onClick?: () => void
      disabled?: boolean
      danger?: boolean
      tooltip?: string
      testid?: string
    }
  | { key: string; separator: true }

interface RowActionMenuProps {
  items: RowActionItem[]
  triggerLabel: string
  triggerTestId?: string
}

const MENU_WIDTH = 176 // tailwind w-44

/**
 * 경량 행 액션 드롭다운(접근성 강화). 트리거 클릭 토글, 바깥 클릭(mousedown)·Escape 로 닫는다.
 * 메뉴는 createPortal 로 document.body 에 fixed 포지셔닝 — 테이블 래퍼의 overflow-hidden 에
 * 잘리지 않는다(마지막 행에서도 하단 항목까지 노출). 하단 공간이 좁으면 위로 펼치고,
 * 그래도 넘치면 maxHeight + 내부 스크롤로 처리. 스크롤/리사이즈 시 닫는다.
 */
export function RowActionMenu({ items, triggerLabel, triggerTestId }: RowActionMenuProps) {
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState<{ left: number; top?: number; bottom?: number; maxHeight: number }>({
    left: 0,
    maxHeight: 0,
  })
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    const vh = window.innerHeight
    const spaceBelow = vh - rect.bottom
    const spaceAbove = rect.top
    const openUp = spaceBelow < 260 && spaceAbove > spaceBelow
    const left = Math.max(8, Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8))
    setCoords(
      openUp
        ? { left, bottom: vh - rect.top + 4, maxHeight: spaceAbove - 16 }
        : { left, top: rect.bottom + 4, maxHeight: spaceBelow - 16 },
    )
  }, [open])

  useEffect(() => {
    if (!open) return
    const onMouseDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    const onScroll = (e: Event) => {
      const target = e.target
      if (target instanceof Node && menuRef.current?.contains(target)) return
      setOpen(false)
    }
    const onResize = () => setOpen(false)
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKeyDown)
    window.addEventListener('resize', onResize)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('resize', onResize)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [open])

  return (
    <div className="inline-block text-left">
      <button
        ref={triggerRef}
        type="button"
        data-testid={triggerTestId}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
      >
        <span>{triggerLabel}</span>
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{
              position: 'fixed',
              left: coords.left,
              top: coords.top,
              bottom: coords.bottom,
              maxHeight: coords.maxHeight > 0 ? coords.maxHeight : undefined,
            }}
            className="z-50 w-44 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg py-1"
          >
            {items.map((item) => {
              if ('separator' in item) {
                return <div key={item.key} className="my-1 border-t border-gray-100" role="separator" />
              }
              return (
                <button
                  key={item.key}
                  type="button"
                  role="menuitem"
                  data-testid={item.testid}
                  disabled={item.disabled}
                  title={item.tooltip}
                  onClick={() => {
                    if (item.disabled) return
                    item.onClick?.()
                    setOpen(false)
                  }}
                  className={`block w-full px-3 py-1.5 text-left text-sm ${
                    item.disabled
                      ? 'text-gray-300 cursor-not-allowed'
                      : item.danger
                        ? 'text-red-600 hover:bg-red-50'
                        : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {item.label}
                </button>
              )
            })}
          </div>,
          document.body,
        )}
    </div>
  )
}
